import React, { useState } from "react";
import HeaderSection from "../components/shared/HeaderSection";
import FormInput from '../components/form/FormInput';
import FormTextarea from '../components/form/FormTextarea';
import { FaEnvelope, FaPhoneAlt, FaMapMarkerAlt, FaClock } from "react-icons/fa";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setResponseMessage("");

    try {
      const response = await fetch(`${process.env.REACT_APP_CONTACT_SERVICE}/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setResponseMessage("Thank you for contacting us! We'll get back to you soon.");
        setFormData({ name: "", email: "", phone: "", message: "" }); // Reset form
        setTimeout(() => {
          setResponseMessage("");
        }, 15000);
      } else {
        setResponseMessage("Something went wrong. Please try again later.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setResponseMessage("An error occurred. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-gray-100 to-white text-white">
      {/* Header Section */}
      <HeaderSection
        image="/images/bg-images/contact-bg.jpeg"
        header="Contact Us"
        subHeader="Let's Build Something Great Together – Get in Touch Today!"
      />

      {/* Main Content */}
      <div className="py-16 px-6">
        <div className="container mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Details */}
          <div className="p-8 bg-white rounded-2xl shadow-lg max-w-2xl mx-auto">
            {/* <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Get in Touch</h2> */}
            <h1 className="text-2xl sm:text-4xl font-extrabold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              Get in Touch
            </h1>
            <p className="text-gray-600">
              Reach out to us for inquiries, collaborations, or service requests. We’d love to hear from you!
            </p>
            <div className="py-1 px-1">
              <div className="container mx-auto max-w-6xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
                
                {/* Email Tile */}
                <div className="p-6 bg-white rounded-lg shadow-lg text-center">
                  <div className="flex justify-center mb-2">
                    <span className="p-4 bg-blue-100 text-blue-600 rounded-full">
                      <FaEnvelope className="text-3xl" />
                    </span>
                  </div>
                  <h3 className="text-lg text-gray-800 font-bold mb-1">Email</h3>
                  <p className="text-gray-600">
                    <a
                      href="mailto:info@hithertech.co.uk"
                      className="text-blue-600 hover:underline"
                    >
                      info@hithertech.co.uk
                    </a>
                  </p>
                </div>

                {/* Phone Tile */}
                <div className="p-6 bg-white rounded-lg shadow-lg text-center">
                  <div className="flex justify-center mb-2">
                    <span className="p-4 bg-purple-100 text-purple-600 rounded-full">
                      <FaPhoneAlt className="text-3xl" />
                    </span>
                  </div>
                  <h3 className="text-lg text-gray-800 font-bold mb-1">Phone</h3>
                  <p className="text-gray-600">
                    <a
                      href="tel:+442039165115"
                      className="text-purple-600 hover:underline"
                    >
                      +44 203 916 5115 (UK)
                    </a>
                  </p>
                </div>

                {/* Address Tile */}
                <div className="p-6 bg-white rounded-lg shadow-lg text-center">
                  <div className="flex justify-center mb-2">
                    <span className="p-4 bg-blue-100 text-blue-600 rounded-full">
                      <FaMapMarkerAlt className="text-3xl" />
                    </span>
                  </div>
                  <h3 className="text-lg text-gray-800 font-bold mb-1">Address</h3>
                  <p className="text-blue-600">
                    Suite RA01, 195-197 Wood Street, London, E17 3NU
                  </p>
                </div>

                {/* Hours Tile */}
                <div className="p-6 bg-white rounded-lg shadow-lg text-center">
                  <div className="flex justify-center mb-2">
                    <span className="p-4 bg-purple-100 text-purple-600 rounded-full">
                      <FaClock className="text-3xl" />
                    </span>
                  </div>
                  <h3 className="text-lg text-gray-800 font-bold mb-1">Hours</h3>
                  <p className="text-purple-600">Monday–Friday, 9am – 6pm (GMT)</p>
                </div>

              </div>
            </div>
          </div>  


          {/* Contact Form */}
          <div className="bg-gradient-to-br from-blue-400 to-slate-950 text-white rounded-lg p-6 shadow-lg">
            <h2 className="text-3xl font-bold mb-4">Send Us a Message</h2>
            <form className="space-y-6" onSubmit={handleSubmit}>
              <FormInput
                id="name"
                label="Name"
                type="text"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                required
              />
              <FormInput
                id="email"
                label="Email"
                type="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleChange}
                required
              />
              <FormInput
                id="phone"
                label="Contact Number"
                type="tel"
                placeholder="Your Contact Number"
                value={formData.phone}
                onChange={handleChange}
                required
              />
              <FormTextarea
                id="message"
                label="Message"
                placeholder="Your Message"
                value={formData.message}
                onChange={handleChange}
                required
              />
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </form>
            {responseMessage && <p className="mt-4 text-center">{responseMessage}</p>}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;